import { useMemo } from 'react'
import { diffLines, type Change } from 'diff'

import { cn } from '@/lib/utils'

interface DiffViewerProps {
  fromBody: string
  toBody: string
  fromLabel?: string
  toLabel?: string
  view?: 'unified' | 'split'
}

/**
 * Render a unified or side-by-side diff between two prompt bodies. Wraps the
 * `diff` npm package's `diffLines` so the parent can pass plain strings and
 * receive a styled, scrollable block. Each chunk is annotated with
 * `data-change="added|removed|context"` for testability.
 *
 * Story 3.7 §AC #9: side-by-side ou unified diff. We expose both modes via
 * the `view` prop; the page can offer a toggle to the operator.
 */
export function DiffViewer({
  fromBody,
  toBody,
  fromLabel = 'Versão anterior',
  toLabel = 'Versão atual',
  view = 'unified',
}: DiffViewerProps): React.ReactElement {
  const changes = useMemo<Change[]>(() => diffLines(fromBody, toBody), [fromBody, toBody])

  if (view === 'split') {
    return (
      <div
        className="grid gap-3 md:grid-cols-2"
        role="region"
        aria-label="Comparação lado a lado entre versões"
      >
        <DiffPanel label={fromLabel} side="from" changes={changes} />
        <DiffPanel label={toLabel} side="to" changes={changes} />
      </div>
    )
  }

  return (
    <div
      className="rounded-md border bg-muted/20 p-3 font-mono text-xs leading-relaxed"
      role="region"
      aria-label="Diff unificado entre versões"
    >
      <header className="mb-2 flex items-center gap-3 text-[11px] uppercase tracking-wide text-muted-foreground">
        <span>{fromLabel}</span>
        <span>→</span>
        <span>{toLabel}</span>
      </header>
      <pre className="whitespace-pre-wrap break-words">
        {changes.length === 0 || (changes.length === 1 && changes[0]?.added !== true && changes[0]?.removed !== true) ? (
          <span data-change="context" className="text-muted-foreground">
            Sem diferenças entre as versões.
          </span>
        ) : (
          changes.map((part, idx) => (
            <DiffLine key={`${part.value.length}-${idx}`} part={part} />
          ))
        )}
      </pre>
    </div>
  )
}

function DiffLine({ part }: { part: Change }): React.ReactElement {
  const kind = part.added === true ? 'added' : part.removed === true ? 'removed' : 'context'
  const className =
    kind === 'added'
      ? 'block bg-emerald-500/10 text-emerald-700 dark:text-emerald-300'
      : kind === 'removed'
        ? 'block bg-red-500/10 text-red-700 dark:text-red-300'
        : 'block text-muted-foreground'
  const prefix = kind === 'added' ? '+ ' : kind === 'removed' ? '- ' : '  '
  return (
    <span data-change={kind} className={className}>
      {part.value
        .split('\n')
        .filter((l: string, i: number, arr: string[]) => l !== '' || i < arr.length - 1)
        .map((line: string, i: number) => (
          <span key={i} className="block">
            {prefix}
            {line}
          </span>
        ))}
    </span>
  )
}

function DiffPanel({
  label,
  side,
  changes,
}: {
  label: string
  side: 'from' | 'to'
  changes: ReadonlyArray<Change>
}): React.ReactElement {
  return (
    <div className="rounded-md border bg-muted/20 p-3 font-mono text-xs leading-relaxed">
      <header className="mb-2 text-[11px] uppercase tracking-wide text-muted-foreground">
        {label}
      </header>
      <pre className="whitespace-pre-wrap break-words">
        {changes.map((part, idx) => {
          const kind =
            part.added === true ? 'added' : part.removed === true ? 'removed' : 'context'
          // The "from" panel hides additions; the "to" panel hides removals.
          if (side === 'from' && kind === 'added') return null
          if (side === 'to' && kind === 'removed') return null
          const className = cn(
            'block',
            kind === 'added' && 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300',
            kind === 'removed' && 'bg-red-500/10 text-red-700 dark:text-red-300',
            kind === 'context' && 'text-muted-foreground',
          )
          return (
            <span key={`${side}-${idx}`} data-change={kind} className={className}>
              {part.value}
            </span>
          )
        })}
      </pre>
    </div>
  )
}

export default DiffViewer
